import BaseModel, { type IResourceName } from 'volta/models/base-model';
import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api';
import {
  collectionFiltersFromJsonApiFilters,
  collectionFiltersToQueryParams,
  collectionViewToQueryParams
} from 'volta/utils/filters-utils';
import { attr } from '@ember-data/model';

import { Type } from '@warp-drive/core-types/symbols';
import { cached } from '@glimmer/tracking';
import { DEFAULT_PLANNING_SETTING_DATA } from './update-planning-settings-cmd';

import type { ICollectionViewDefinition } from './types/collection-view';
import type { IPlanningSettingData } from './types/planning-settings';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { IUpdatePlanningSettingRule } from 'volta/models/types/planning-setting-rule';

export const PlanningSettingRuleResourceName: IResourceName = {
  singular: 'planningSettingRule',
  plural: 'planningSettingRules'
};

export enum PlanningSettingRuleCommands {
  CreatePlanningSettingRule = 'CreatePlanningSettingRule',
  UpdatePlanningSettingRule = 'UpdatePlanningSettingRule',
  DeletePlanningSettingRule = 'DeletePlanningSettingRule',
  ReorderPlanningSettingRules = 'ReorderPlanningSettingRules'
}

export default class PlanningSettingRule extends BaseModel {
  // Collection Commands
  // ~~~~~

  static createPlanningSettingRule = collectionCommand<
    IUpdatePlanningSettingRule,
    PlanningSettingRule,
    typeof PlanningSettingRule
  >(PlanningSettingRuleCommands.CreatePlanningSettingRule);

  static reorderPlanningSettingRules = collectionCommand<
    { ids: string[] },
    string[],
    typeof PlanningSettingRule
  >(PlanningSettingRuleCommands.ReorderPlanningSettingRules);

  // Attributes
  // ~~~~~

  @attr('string')
  name!: string;

  @attr('string')
  description?: string;

  @attr('number')
  ruleOrder!: number;

  @attr('number')
  skuCount!: number;

  @attr({
    defaultValue: () => ({})
  })
  filters!: IJsonApiQuery['filter'];

  @attr({ defaultValue: () => DEFAULT_PLANNING_SETTING_DATA })
  settings!: Partial<IPlanningSettingData>;

  @attr('boolean', { defaultValue: true })
  enabled!: boolean;

  get collectionViewQueryParams() {
    return { filters: collectionFiltersToQueryParams(this.collectionViewFilters) };
  }

  @cached
  get collectionViewFilters() {
    return collectionFiltersFromJsonApiFilters(this.filters);
  }

  /**
   * Get the number of settings properties that are defined
   */
  get settingsCount() {
    return Object.values(this.settings).filter((v) => v).length;
  }

  get isBuffer() {
    return this.settings.methodology === 'BUFFER';
  }

  get isRolling() {
    return this.isBuffer && this.settings.aduType === 'ROLLING';
  }

  jsonApiQuery(collectionView: ICollectionViewDefinition) {
    return collectionViewToQueryParams({ filters: this.collectionViewFilters }, {}, collectionView);
  }

  toCmd(): IUpdatePlanningSettingRule {
    const { name, description, filters, settings, enabled } = this;
    return { name, description, filters, settings, enabled };
  }

  // Instance Commands
  // ~~~~~

  updatePlanningSettingRule = command<IUpdatePlanningSettingRule, PlanningSettingRule>(
    PlanningSettingRuleCommands.UpdatePlanningSettingRule
  );

  deletePlanningSettingRule = command<any, PlanningSettingRule>(
    PlanningSettingRuleCommands.DeletePlanningSettingRule
  );

  [Type] = 'planning-setting-rule' as const;
}
